import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Checkbox,
  FormControlLabel,
  Link,
  CircularProgress,
} from "@mui/material";

const CheckoutForm = ({ cart, onBack, onSuccess }) => {
  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    payment: "sslcommerz",
  });

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // 1. AUTO-FILL LOGIC (Matches your new productType)
 useEffect(() => {
    // Priority 1: Motor Items
    const motorItem = cart.find(
      (item) => ["Motor Insurance", "MC Insurance", "Commercial Vehicle Insurance", "Motor Liability Insurance"].includes(item.productType)
    );
    // Priority 2: Travel Items
    const travelItem = cart.find((item) => item.productType === "Travel Health Insurance");

    if (motorItem && motorItem.data?.ownerName) {
      setCustomer((prev) => ({ ...prev, name: motorItem.data.ownerName }));
    } else if (travelItem && travelItem.data?.passportName) {
      setCustomer((prev) => ({ ...prev, name: travelItem.data.passportName }));
    }
  }, [cart]);

  const handleChange = (e) =>
    setCustomer({ ...customer, [e.target.name]: e.target.value });

  // 2. TOTAL CALCULATION (Updated for GROSS_PREMIUM)
  const total = cart.reduce((sum, item) => {
    const price = item.data.GROSS_PREMIUM || item.data.price || 0;
    return sum + price;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreeTerms) {
      alert("⚠️ Please agree to the Terms & Conditions.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    Object.entries(customer).forEach(([key, val]) => formData.append(key, val));

    cart.forEach((item, idx) => {
      formData.append(`products[${idx}][type]`, item.productType);
      Object.keys(item.data).forEach((key) => {
        const value = item.data[key];
        if (value instanceof File) formData.append(`products[${idx}][${key}]`, value);
        else if (typeof value === "object" && value !== null)
          formData.append(`products[${idx}][${key}]`, JSON.stringify(value));
        else formData.append(`products[${idx}][${key}]`, value);
      });
    });

    const debugObj = {};
    formData.forEach((value, key) => {
      // If it's a file, just show the name, otherwise stringify won't work well
      debugObj[key] = value instanceof File ? `File: ${value.name}` : value;
    });

    //alert(JSON.stringify(debugObj, null, 2));

    try {
      const res = await fetch("https://www.bgicdigital.com/admin/backend_api.php", {
     // const res = await fetch("http://localhost/bgic-store/admin/backend_api.php", {
        method: "POST",
        body: formData,
      });
      const result = await res.json();

      if (result.success) {
        if (customer.payment === "sslcommerz" && result.payment_url) {
          window.location.href = result.payment_url;
        } else {
          alert("✅ Order submitted successfully!");
          if (onSuccess) onSuccess();
        }
      } else {
        alert("❌ Error: " + (result.error || "Unknown"));
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      alert("⚠️ Server error");
      setLoading(false);
    }
  };

  // Helper to make keys readable in the table
  const formatLabel = (key) => {
    const labels = {
      ownerName: "Owner Name",
      vehicleNumber: "Vehicle No",
      cc_capacity: "Engine CC",
      fiv: "Sum Insured",
      mfgYear: "Year"
    };
    return labels[key] || key.replace(/_/g, ' ').toUpperCase();
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pb: 10 }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar sx={{ justifyContent: "center" }}>
          <Box component="a" href="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none", color: "inherit" }}>
            <img src="/logo192.png" alt="Logo" style={{ height: 40, marginRight: 12 }} />
            <Typography variant="h6" fontWeight={700}>BGIC Digital Store</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button 
          variant="text" 
          disabled={loading} 
          onClick={() => (onBack ? onBack() : window.history.back())} 
          sx={{ mb: 2, fontWeight: 600 }}
        >
          ← Return to Cart
        </Button>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, boxShadow: "0 8px 24px rgba(0,0,0,0.05)" }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>Billing Details</Typography>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 4, fontWeight: 700 }}>
                  Amount to Pay: ৳ {total.toLocaleString()}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField 
                        label="Full Name" name="name" value={customer.name} onChange={handleChange} 
                        required fullWidth disabled={loading}
                        slotProps={{ inputLabel: { sx: { "& .MuiFormLabel-asterisk": { color: "red" } } } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField 
                        label="Email" name="email" type="email" value={customer.email} onChange={handleChange} 
                        required fullWidth disabled={loading}
                        slotProps={{ inputLabel: { sx: { "& .MuiFormLabel-asterisk": { color: "red" } } } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField 
                        label="Phone Number" name="phone" value={customer.phone} onChange={handleChange} 
                        required fullWidth disabled={loading}
                        slotProps={{ inputLabel: { sx: { "& .MuiFormLabel-asterisk": { color: "red" } } } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField 
                        label="Full Address" name="address" value={customer.address} onChange={handleChange} 
                        required fullWidth disabled={loading}
                        slotProps={{ inputLabel: { sx: { "& .MuiFormLabel-asterisk": { color: "red" } } } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        select label="Payment Method" name="payment" value={customer.payment} 
                        onChange={handleChange} fullWidth disabled={loading}
                      >
                       {/* <MenuItem value="cod">Cash on Delivery</MenuItem>*/}
                        <MenuItem value="sslcommerz">Online Payment (SSLCommerz)</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                          control={<Checkbox checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} disabled={loading} />}
                            label={
                            <Typography variant="body2" color="textSecondary">
                              I agree to the <Link href="/term" target="_blank">Terms & Conditions</Link>, <Link href="/refund-cancellation" target="_blank">Refund Policy</Link>, and <Link href="/privacy-policy" target="_blank">Privacy Policy</Link>.
                            </Typography>
                            }
                        />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit" variant="contained" color="primary" fullWidth
                    disabled={loading}
                    sx={{ mt: 4, py: 2, fontSize: 17, fontWeight: 700, borderRadius: 2 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Confirm Order & Pay"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* TABLE PREVIEW */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Order Summary</Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Table>
                <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{item.productType}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {Object.entries(item.data).map(([k, v]) => {
                            // Only show primary details in the summary table
                            const showKeys = ['ownerName', 'vehicleNumber', 'cc_capacity', 'fiv'];
                            if (!showKeys.includes(k)) return null;
                            return (
                              <Typography key={k} variant="caption" display="block">
                                <strong>{formatLabel(k)}:</strong> {String(v)}
                              </Typography>
                            );
                          })}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ৳ {(item.data.GROSS_PREMIUM || item.data.price || 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CheckoutForm;